"use client";

import type { PracticeHand } from "../practice";
import { handColorOf } from "./practice-keyboard";

/**
 * Two pills, `G` and `D`, whose fill **is** the instruction.
 *
 * Three states, told apart without colour — filled, outlined with a bar,
 * outlined — because the two hand hues are 1.83:1 apart (1.24:1 once tinted)
 * where WCAG asks 3:1 between adjacent meaningful graphics, and they collapse
 * onto the same blue-grey in deuteranopia. A player who cannot separate the
 * two hues still has to know who plays.
 *
 * `holds` is the state that did not exist: 39.9% of groups ask a hand to
 * *hold* while the other plays, and the screen used to tell that hand it was
 * doing nothing.
 *
 * Shared by the both-hands run, which reads it group by group, and by the run
 * in time, which reads it once per pass — same object, so "whose turn" means
 * the same thing on both screens.
 */

export type HandPillState = "plays" | "holds" | "waits";

const HAND_LETTER: Readonly<Record<PracticeHand, string>> = {
  L: "G",
  R: "D",
};

const HAND_LABEL: Readonly<Record<PracticeHand, string>> = {
  L: "main gauche",
  R: "main droite",
};

export function HandPills({
  stateOf,
  marginBottom = 12,
}: {
  readonly stateOf: (hand: PracticeHand) => HandPillState;
  readonly marginBottom?: number;
}) {
  return (
    <div className="flex items-center" style={{ gap: 10, marginBottom }}>
      {(["L", "R"] as const).map((hand) => (
        <HandPill key={hand} hand={hand} state={stateOf(hand)} />
      ))}

      <span className="sr-only">
        {(["L", "R"] as const)
          .map((hand) => `${HAND_LABEL[hand]} : ${wordFor(stateOf(hand))}`)
          .join(", ")}
      </span>
    </div>
  );
}

export function HandPill({
  hand,
  state,
}: {
  readonly hand: PracticeHand;
  readonly state: HandPillState;
}) {
  const color = handColorOf(hand);
  const plays = state === "plays";
  const holds = state === "holds";

  return (
    <span className="flex flex-col items-center" style={{ gap: 3 }} aria-hidden>
      <span
        className="flex items-center justify-center font-mono"
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          background: plays ? color : "transparent",
          border: plays
            ? "none"
            : `1.5px solid ${holds ? color : "var(--color-border-subtle)"}`,
          color: plays
            ? "#0d0d12"
            : holds
              ? color
              : "var(--color-text-secondary)",
          boxShadow: plays ? `0 0 6px ${color}` : "none",
        }}
      >
        {HAND_LETTER[hand]}
      </span>

      <span
        style={{
          width: 16,
          height: 2,
          borderRadius: 999,
          background: holds ? color : "transparent",
        }}
      />
    </span>
  );
}

function wordFor(state: HandPillState): string {
  if (state === "plays") {
    return "joue";
  }

  if (state === "holds") {
    return "tient";
  }

  return "attend";
}
